import { Box, Grid, makeStyles, Typography } from '@material-ui/core'
import moment from 'moment'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { Order, OrderUnit } from 'src/api/models'
import { SkuPathResponse } from 'src/api/models/SkuPathResponse'
import { skuit } from 'src/services/skuit'
import { Barcode } from 'src/components/Barcode'
import { useQueryPaginator } from 'src/components/Table'
import useProfileContext from 'src/context/ProfileContext'


const useStyles = makeStyles((theme) => ({
  card: {
    border: '1px solid gray',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    margin: theme.spacing(2),
    maxWidth: '96%',
  },
  section: {
    minHeight: '100%',
    paddingTop: theme.spacing(2),
  },
  units: {
    fontSize: theme.spacing(4),
    fontWeight: 'bold',
  },
  break: {
    wordBreak: 'break-word',
  },
}))

interface PickTicketProps {
  order: Order
  onReadyToPrint?: () => void
}

interface UnitCardProps {
  orderUnit: OrderUnit
  skuPaths: string[]
}

const UnitCard = (props: UnitCardProps) => {
  const { orderUnit, skuPaths } = props
  const classes = useStyles()
  const barcode = { barcode: orderUnit.sku?.guid || '', type: 'datamatrix', displayName: '' }

  return (
    <div className="card-break ">
      <Grid container spacing={3} component="article" className={classes.card}>
        <Grid item xs={12}>
          <Grid container spacing={4} alignItems="stretch" alignContent="stretch">
            <Grid item xs={2}>
              <Barcode barcode={barcode} />
            </Grid>
            <Grid item xs={5}>
              <Grid
                container
                direction="column"
                alignItems="flex-start"
                justify="flex-end"
                spacing={2}
                className={classes.section}
              >
                <Grid item>
                  <Typography variant="body1" className={classes.break}>
                    {orderUnit.sku?.product?.guid}
                  </Typography>
                </Grid>
                {orderUnit.sku?.product?.primaryBarcode && (
                  <Grid item>
                    <Typography variant="body1">
                      [{orderUnit.sku.product.primaryBarcode.type}]{' '}
                      {orderUnit.sku.product.primaryBarcode.barcode}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={5}>
              <Grid
                container
                direction="column"
                alignItems="flex-start"
                justify="flex-start"
                spacing={2}
                className={classes.section}
              >
                <Grid item>
                  <Typography variant="body1">
                    [{orderUnit?.sku?.sku}] {orderUnit?.sku?.product?.name}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" className={classes.units}>
                    X {orderUnit?.numUnits}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={0}>
            {skuPaths &&
              skuPaths.length > 0 &&
              skuPaths.map((path) => (
                <Grid item xs={12} key={path}>
                  <Typography variant="body1" component="div">
                    {path}
                  </Typography>
                </Grid>
              ))}
          </Grid>
        </Grid>
      </Grid>
    </div>
  )
}

export const PickTicket = (props: PickTicketProps) => {
  const { order, onReadyToPrint } = props
  const [readyToPrint, setReadyToPrint] = useState(false)
  const [totalUnits, setTotalUnits] = useState<Array<OrderUnit>>([])
  const [skusList, setSkusList] = useState<Array<string>>([])
  const [totalSkuPaths, setTotalSkuPaths] = useState<Array<SkuPathResponse>>([])

  const [allUnitsReady, setAllUnitsReady] = useState(false)
  const [allSkuPathsReady, setAllSkuPathsReady] = useState(false)

  const profileContext = useProfileContext()


  const unitsPaginator = useQueryPaginator('order-units-list', skuit.sc.parcel.orders.units.list, {
    params: { page_size: 100, orders: order.guid },
  })
  const skuPathPaginator = useQueryPaginator('sku-paths', skuit.sc.skus.storagePaths, {
    params: { page_size: 100, parcelorders: order.guid, skus: skusList },
  })

  const {
    loading: loadingUnits,
    data: unitsData,
    page: unitsPage,
    handlePageChange: handleUnitsPageChange,
  } = unitsPaginator
  const {
    loading: loadingSkuPaths,
    data: skuPathsData,
    page: skuPathsPage,
    handlePageChange: handleSkuPathsPageChange,
  } = skuPathPaginator

  useEffect(() => {
    if (!loadingUnits && unitsData && !allUnitsReady) {
      const { next, results } = unitsData
      setTotalUnits([...totalUnits, ...results])
      if (next !== null) {
        const event = {
          target: {
            value: 'z',
          },
        } as ChangeEvent<HTMLInputElement>
        handleUnitsPageChange(event, unitsPage + 1)
      } else {
        setAllUnitsReady(true)
      }
    }
  }, [unitsData, allUnitsReady, handleUnitsPageChange, loadingUnits, totalUnits, unitsPage])

  useEffect(() => {
    if (allUnitsReady && skusList.length === 0 && totalUnits.length > 0) {
      const totalSkusList = totalUnits.map((sku) => sku.guid ?? '')
      setSkusList(totalSkusList)
    } else if (allUnitsReady && skusList.length === 0 && totalUnits.length === 0) {
      setReadyToPrint(true)
      onReadyToPrint?.()
    }
  }, [allUnitsReady, skusList.length, totalUnits, onReadyToPrint])

  useEffect(() => {
    if (
      allUnitsReady &&
      !loadingSkuPaths &&
      !allSkuPathsReady &&
      skuPathsData &&
      skusList.length > 0
    ) {
      const { results, next } = skuPathsData
      setTotalSkuPaths((actualSkuPaths) => [...actualSkuPaths, ...results])
      if (next !== null) {
        const event = {
          target: {
            value: 'z',
          },
        } as ChangeEvent<HTMLInputElement>
        handleSkuPathsPageChange(event, skuPathsPage + 1)
      } else {
        setAllSkuPathsReady(true)
        setReadyToPrint(true)
      }
    }
  }, [
    allUnitsReady,
    skuPathsData,
    allSkuPathsReady,
    handleSkuPathsPageChange,
    loadingSkuPaths,
    skuPathsPage,
    skusList.length,
    onReadyToPrint,
  ])

  useEffect(() => {
    setTotalUnits([])
    setTotalSkuPaths([])
    setSkusList([])
    setAllUnitsReady(false)
    setAllSkuPathsReady(false)
  }, [order.guid])

  useEffect(() => {
    if (readyToPrint) {
      onReadyToPrint?.()
    }
  }, [readyToPrint, onReadyToPrint])

  if (!readyToPrint) {
    return <div>Pick Ticket not ready to print</div>
  }

  return (
    <div className="always-page-break">
      <Box px={20}>
        <Box textAlign="center">
          <Typography variant="caption" align="center">
            Pick Ticket for {order.guid}, printed on {moment().format('lll')} by {profileContext.profile?.firstName} {profileContext.profile?.lastName}
          </Typography>
        </Box>
        <Box>
          <Typography variant="h3" gutterBottom align="center">
            Order #{order?.number}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle1" align="center">
            Fullfilled on {moment().format('ll')}
          </Typography>
        </Box>
      </Box>
      <div className="item-container">
        {allUnitsReady && allSkuPathsReady && totalUnits.length > 0 && totalSkuPaths && (
          <Box marginTop={6}>
            <Typography variant="h4" align="center" gutterBottom>
              Product (
              {totalUnits?.reduce(
                (sum, shipmentUnit) => sum + (shipmentUnit.numUnits ? +shipmentUnit.numUnits : 0),
                0
              )}{' '}
              total units)
            </Typography>
            {totalUnits.map((unit) => (
              <UnitCard
                orderUnit={unit}
                key={unit?.guid}
                skuPaths={
                  totalSkuPaths.find((skuPathResponse) => skuPathResponse.sku?.guid === unit?.guid)
                    ?.paths || []
                }
              />
            ))}
          </Box>
        )}
      </div>
    </div>
  )
}
